"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { useQuery } from "@apollo/client";
import defaultProfile from "@/images/default-profile.jpg";
import { SUGGESTED_USERS } from "@/api/apollo-client/queries/Queries";
import SkeletonLoader from "./SkeletonLoader";
import FollowButton, { ButtonVariant } from "./followButton";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/authContext";
import { HoverableMention } from "./HoverableMention";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface MentionedUser {
  id: string;
  name: string;
  display_name?: string;
  profile_picture?: {
    url: string;
    thumbnail_url: string;
  };
}

export default function MobileSuggestedUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<MentionedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { data, error } = useQuery(SUGGESTED_USERS, {
    variables: {
      input: {
        user_id: user?.id,
        pagination: {
          first: 5,
          after_name: null,
        },
      },
    },
    skip: !user,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.suggestedUsers) {
      setIsLoading(true);

      const timer = setTimeout(() => {
        setUsers(data.suggestedUsers);
        setIsLoading(false);
      }, 1);

      return () => clearTimeout(timer);
    }
  }, [data]);

  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollButtons);
      window.addEventListener("resize", checkScrollButtons);
      return () => {
        container.removeEventListener("scroll", checkScrollButtons);
        window.removeEventListener("resize", checkScrollButtons);
      };
    }
  }, [users, checkScrollButtons]);

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 280;
      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      setTimeout(checkScrollButtons, 100);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 280;
      container.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
      setTimeout(checkScrollButtons, 100);
    }
  };

  return (
    <div className="md:hidden mb-4">
      <h2 className="text-muted-foreground font-semibold px-4 py-1 mt-5 mb-2 text-lg dark:bg-gray-800/50 rounded-2xl w-fit shadow-md dark:shadow-gray-100/10">
        You might like
      </h2>

      {isLoading && (
        <div className="px-4">
          <SkeletonLoader variant="list" count={3} showShimmer={true} />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 px-4">Failed to load suggestions.</p>
      )}

      {!isLoading && users.length > 0 && (
        <div className="relative">
          <div className="overflow-hidden">
            <div
              ref={scrollContainerRef}
              className="flex gap-3 overflow-x-auto pb-2 scroll-smooth pr-4 pl-1 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {users.map((suggestedUser) => (
                <div
                  key={suggestedUser.id}
                  className="flex-shrink-0 w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex flex-col items-center gap-3"
                >
                  <Link
                    href={`/profile/${suggestedUser.name}`}
                    className="flex flex-col items-center gap-2 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/profile/${suggestedUser.name}`);
                    }}
                  >
                    <Image
                      src={
                        suggestedUser?.profile_picture?.thumbnail_url ||
                        suggestedUser?.profile_picture?.url ||
                        defaultProfile
                      }
                      alt={suggestedUser?.name || ""}
                      width={80}
                      height={80}
                      className="w-20 h-20 object-cover rounded-full"
                    />
                    <div className="flex flex-col items-center gap-1 w-full">
                      <HoverableMention
                        user={suggestedUser}
                        variant="whoToFollow"
                      >
                        <p
                          className="font-bold text-sm text-center truncate max-w-32"
                          title={suggestedUser?.display_name}
                        >
                          {suggestedUser?.display_name || suggestedUser?.name}
                        </p>
                        <p
                          className="text-xs text-muted-foreground text-center truncate max-w-20"
                          title={`@${suggestedUser.name.toLowerCase().replace(/\s+/g, "")}`}
                        >
                          @
                          {suggestedUser.name.toLowerCase().replace(/\s+/g, "")}
                        </p>
                      </HoverableMention>
                    </div>
                  </Link>

                  <div className="w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center">
                      <FollowButton
                        _user={suggestedUser}
                        variant={ButtonVariant.TEXT_ONLY}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 rounded-full p-2 shadow-lg transition-all opacity-80 hover:opacity-100"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}

          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 rounded-full p-2 shadow-lg transition-all opacity-80 hover:opacity-100"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
